import {Date as ApiDate} from 'gabi-api-ts/v2/common/date/date';
import {SignalType, ThresholdType} from 'gabi-api-ts/v2/common/signals/signals';
import {PatientType} from 'gabi-api-ts/v2/patient/common/patient_common';
import {TypedEventsList} from 'gabi-api-ts/v2/signal/query/signal_query';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import Styled from 'styled-components';

import GuidedTourHealthReport from '@/components/business/analytics/guidedTour/guided-tour-health-report';
import {FormattedAggregatedSignalDataBlock, HealthReportAggregatedSignal} from '@/components/business/analytics/health-report/health-report-aggregated-signal';
import {HealthReportDuration} from '@/components/business/analytics/health-report/health-report-duration';
import {HealthReportEventsList} from '@/components/business/analytics/health-report/health-report-events-list';
import {EventPerHourMerged, HealthReportEventsPerHour} from '@/components/business/analytics/health-report/health-report-events-per-hour';
import {HealthReportNoRecording} from '@/components/business/analytics/health-report/health-report-no-recording';
import {HealthReportWrapper} from '@/components/business/analytics/health-report/health-report-wrapper';
import ColoredContainer from '@/components/layout/colored-container';
import LoadingView from '@/components/static/loading-view';
import {HealthReportDashboardTypeEnum} from '@/enum/health-report-dashboard-type-enum';
import {usePatientData} from '@/hooks/backend/use-patient-data';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {defaultEventConfiguration} from '@/model/defaults/default-event-configuration';
import timelineIcon from '@/public/icons/timeline-icon.svg';
import {formatApiDateToJSDate} from '@/services/api-requests/requests-utils';
import {ignoreVP39} from '@/services/grpc/grpc-error-handlers';
import {PatientContextService} from '@/services/patient-context-service';
import {useTutorialStatus} from '@/services/tutorial-service';
import {colorPalette, colors, medias} from '@/themes/darkmode';
import {apiDateToday, isAfter, subDays} from '@/util/apidate-util';
import {getSignalTypeUnit} from '@/util/signal-type-util';
import {formatApiDateForUrl} from '@/util/url-util';

type HealthReportLastAvailableNightRealtimeProps = {
    id_patient: string;
    className: string,
};

function HealthReportLastAvailableNightRealtimeUnstyled(props: HealthReportLastAvailableNightRealtimeProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const patientId = props.id_patient ?? '';

    const tutorialStatus = useTutorialStatus();

    const [patientDataLoading, patientData] = usePatientData({
        patientId: patientId,
        memoize: true,
    });

    const [signalsEventConfigurationLoading, eventConfiguration] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getEventConfiguration,
        memoize: true,
        data: { patientId: { id: patientId } },
        disabled: patientDataLoading || (patientData?.patientType === PatientType.Adult),
        grpcOptions: {
            errorHandler: ignoreVP39(defaultEventConfiguration),
        },
    });

    let dateFrom: ApiDate = { year: 2020, month: 1, day: 1 };
    let dateTo: ApiDate = { year: 2020, month: 1, day: 1 };
    let dashboardHasContent = false;
    let timelineDate = null;
    if (patientData && patientData.lastRecordingDate) {
        dateFrom = patientData.lastRecordingDate;
        dateTo = patientData.lastRecordingDate;
        timelineDate = formatApiDateForUrl(patientData.lastRecordingDate);
        const lastRecordingDateLimit = subDays(apiDateToday(), 30);
        dashboardHasContent = isAfter(dateFrom, lastRecordingDateLimit);
    }

    // We load the events list here so we know wheter to show the events list block or not
    const [ eventsListLoading, eventsList, /*eventsListError*/ ] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getTypedEventsList,
        memoize: true,
        disabled: !dashboardHasContent || patientDataLoading || !patientData,
        data: {
            patientId: {id: patientId},
            date: dateFrom,
        },
        grpcOptions: {
            errorHandler: ignoreVP39({
                event: [],
            } as TypedEventsList),
        },
    }, [eventConfiguration]);

    useEffect(() => {
        if (patientData) {
            const patientContext = PatientContextService.getPatientContext(patientData);
            if (!patientContext.hasRealTimeMonitoring) {
                const url = `/patients/${props.id_patient}/healthReport/last7days`;
                navigate(url, { replace: true });
            }
        }
    }, [ patientData ]);

    const loading = (
        (signalsEventConfigurationLoading || !eventConfiguration) ||
        (dashboardHasContent && (eventsListLoading || !eventsList)) ||
        (patientDataLoading || !patientData)
    );

    if (loading) {
        return (
            <HealthReportWrapper
                patientId={patientId}
                patientData={patientData}
                activeDashboard={HealthReportDashboardTypeEnum.LAST_24H}
            >
                <LoadingView />
            </HealthReportWrapper>
        );
    }
    //--------------------------------------------------------------------------------------------------------------

    function handleAggregatedSignalChartClick(data: FormattedAggregatedSignalDataBlock) {
        const selectedHour = data.at.time;
        const url = `/patients/${patientId}/timeline/${data.at.date}/${selectedHour}`;
        navigate(url);
    }

    function handleEventsPerHourChartClick(data: EventPerHourMerged) {
        const selectedHour = (data.hour < 10) ? `0${data.hour}:00` : `${data.hour}:00`;
        const url = `/patients/${patientId}/timeline/${formatApiDateForUrl(data.time?.date ?? { year: 2020, month: 1, day: 1 })}/${selectedHour}`;
        navigate(url);
    }

    const patientContext = PatientContextService.getPatientContext(patientData!);

    const hasEvents = patientContext.supportsEvents;
    const hasEventsList = patientContext.supportsEvents && (eventsList?.event?.length ?? 0) > 0;

    return (
        <div className={`${props.className} ${hasEventsList ? 'has-events-list' : ''} ${hasEvents ? 'has-events' : ''}`}>
            <HealthReportWrapper
                patientId={patientId}
                patientData={patientData}
                activeDashboard={HealthReportDashboardTypeEnum.LAST_24H}
            >
                {tutorialStatus.guidedTourHealthReport && (
                    <GuidedTourHealthReport />
                )}

                {dashboardHasContent && (
                    <div className="health-report-switch-wrapper">
                        <HealthReportDuration patientId={patientId} dateFrom={dateFrom} dateTo={dateTo}/>
                        <Link
                            to={`/patients/${patientId}/timeline/${timelineDate}`}
                            id="button-timeline"
                            className="button-link"
                        >
                            {t('healthReportDashboardNavigation.dailyBiometrics') ?? 'Daily biometrics'}
                        </Link>
                    </div>
                )}

                {(dashboardHasContent && eventConfiguration) && (
                    <div className={'health-report-content health-report-content-view'}>
                        <ColoredContainer className="widget-aggregated-signal" color={colorPalette.signalType.pulseRate}>
                            <HealthReportAggregatedSignal
                                patientId={patientId}
                                signalType={SignalType.Signal_PR}
                                date={formatApiDateToJSDate(dateFrom)}
                                signalEventConfiguration={eventConfiguration.configuration!.hr!}
                                onClick={handleAggregatedSignalChartClick}
                            />
                        </ColoredContainer>

                        {!hasEvents ? null : (
                            <ColoredContainer className="widget-events" color={colorPalette.signalType.pulseRate}>
                                <HealthReportEventsPerHour
                                    patientId={patientId}
                                    signalType={SignalType.Signal_PR}
                                    alertTypes={[ ThresholdType.HIGH, ThresholdType.LOW ]}
                                    date={formatApiDateToJSDate(dateFrom)}
                                    subtitle={<span>
                                        {t('healthReport.events.last24h.subtitle', {duration: eventConfiguration.configuration?.hr?.sensibility?.seconds}) ?? '#events/h with aduration of X seconds'}
                                    </span>}
                                    signalEventConfiguration={eventConfiguration.configuration!.hr!}
                                    onClick={handleEventsPerHourChartClick}
                                />
                            </ColoredContainer>
                        )}

                        <ColoredContainer className="widget-aggregated-signal" color={colorPalette.signalType.spo2}>
                            <HealthReportAggregatedSignal
                                patientId={patientId}
                                signalType={SignalType.Signal_SPO2}
                                date={formatApiDateToJSDate(dateFrom)}
                                signalEventConfiguration={eventConfiguration.configuration!.spo2!}
                                onClick={handleAggregatedSignalChartClick}
                            />
                        </ColoredContainer>

                        {!hasEvents ? null : (
                            <ColoredContainer className="widget-events" color={colorPalette.signalType.spo2}>
                                <HealthReportEventsPerHour
                                    patientId={patientId}
                                    signalType={SignalType.Signal_SPO2}
                                    alertTypes={[ ThresholdType.LOW ]}
                                    date={formatApiDateToJSDate(dateFrom)}
                                    subtitle={`${t('global.below') ?? 'Below'} ${eventConfiguration.configuration?.spo2?.low?.value}${getSignalTypeUnit(SignalType.Signal_SPO2)}`}
                                    signalEventConfiguration={eventConfiguration.configuration!.spo2!}
                                    onClick={handleEventsPerHourChartClick}
                                />
                            </ColoredContainer>
                        )}

                        {(hasEventsList && eventsList) && (
                            <HealthReportEventsList
                                patientId={patientId}
                                eventsList={eventsList}
                                eventConfiguration={eventConfiguration}
                            />
                        )}
                    </div>
                )}
                {!dashboardHasContent && (
                    <HealthReportNoRecording patient={patientData} activeDashboard={HealthReportDashboardTypeEnum.LAST_24H} />
                )}
            </HealthReportWrapper>
        </div>
    );
}

//language=SCSS
const HealthReportLastAvailableNightRealtime = Styled(HealthReportLastAvailableNightRealtimeUnstyled)`
& {
    .health-report-switch-wrapper {
        position:relative;
        margin-bottom:20px;
        #button-timeline {
            background: ${colors.buttonCreate.background} url(${timelineIcon}) no-repeat 15px center;
            background-size: 25px;
            position: absolute;
            right: 0;
            top: 0;
            padding: 10px 15px 10px 50px;
            border-radius: 5px;
            &:hover, &:focus {
                color: #fff;
                background-color: ${colors.buttonCreate.backgroundHover};
            }
        }
    }
    
    .health-report-content {
        display: grid;
        grid-template-columns: minmax(0, 2fr);
        &.has-events {
            grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
        }
        @media screen and (${medias.xlMin}) {
            &.has-events.has-events-list {
                grid-template-columns: minmax(0, 2fr) minmax(250px, 1fr) minmax(260px, 1fr);
            }
        }
        
        grid-auto-rows: 340px;
        flex-wrap: nowrap;
        gap: 10px;
    }

    .health-report-events-list {
        display: none;
        @media screen and (${medias.xlMin}) {
            display: block;
        }
        grid-column: 3;
        grid-row: 1;
        grid-row-end: span 2;
    }
    
    .widget-aggregated-signal {
        grid-column: 1;
    }
    
    .widget-events {
        grid-column: 2;
    }
}
`;

export {HealthReportLastAvailableNightRealtime};
